import type { ChannelSource } from '../channels/channel-intake.ts';
import type { ServiceMode } from '../contracts/operating-context.ts';
import {
  createDurabilityCoordinator,
  type DurabilityPort,
  type DurableCheckpoint,
  type OutboxRecord,
} from '../durability/durability.ts';
import { createOperationalFacts } from '../facts/operational-facts.ts';
import { createFulfillmentAuthority } from '../fulfillment/fulfillment-authority.ts';
import type { CanonicalProduct } from '../menu/product.ts';
import { createPaymentAuthority } from '../payment/payment-authority.ts';
import type { BusinessPolicySnapshot } from '../policy/business-policy.ts';
import {
  createPrintAuthority,
  type PrintJobRepository,
  type PrintJobSnapshot,
  type PrinterExecutionPort,
  type PrinterExecutionRequest,
  type PrinterExecutionResult,
} from '../printing/print-authority.ts';
import { createStaffAuthority, type StaffPermission } from '../staff/staff-authority.ts';
import type { StoreSettingsPayload } from '../store-settings/store-settings.ts';
import { createAvailabilityAuthority } from '../availability/availability-authority.ts';
import { composeBusinessPolicyWithAvailability } from '../availability/availability-overlay.ts';
import { createTableAuthority, type QueueClassDefinition, type TableDefinition } from '../table/table-authority.ts';
import type { BusinessRuntimeDependencies } from './business-runtime.ts';
import { composeSmtBusinessRuntime } from './smt-runtime-bootstrap.ts';
import type {
  SmtRuntimeAdapterInputs,
  SmtRuntimeBootstrapResult,
  SmtRuntimeCatalogCategory,
  SmtRuntimePaymentMethod,
} from './smt-runtime-bootstrap-contract.ts';

export interface SmtDeviceGateOperatorSeed {
  readonly staffId: string;
  readonly displayName: string;
  readonly permissions: readonly StaffPermission[];
}

export interface SmtDeviceGateBootstrapSeed {
  readonly storeSettings: StoreSettingsPayload;
  readonly storeReady: boolean;
  readonly businessDate: string;
  readonly basePolicy: BusinessPolicySnapshot;
  readonly products: readonly CanonicalProduct[];
  readonly categories: readonly SmtRuntimeCatalogCategory[];
  readonly paymentMethods: readonly SmtRuntimePaymentMethod[];
  readonly rootActorId: string;
  readonly operator: SmtDeviceGateOperatorSeed;
  readonly tables: readonly TableDefinition[];
  readonly queueClasses: readonly QueueClassDefinition[];
  readonly resolveFulfillmentPolicy: BusinessRuntimeDependencies['resolveFulfillmentPolicy'];
  readonly deviceId: string;
  readonly initialServiceMode?: ServiceMode;
  readonly defaultServiceModeBySource?: Readonly<Partial<Record<ChannelSource, ServiceMode>>>;
  readonly now: () => string;
  readonly nextId: SmtRuntimeAdapterInputs['nextId'];
}

class DeviceGateDurabilityPort implements DurabilityPort {
  private readonly checkpoints = new Map<string, DurableCheckpoint<unknown>>();
  private readonly outbox = new Map<string, OutboxRecord>();

  async loadCheckpoint<TPayload>(key: string): Promise<DurableCheckpoint<TPayload> | null> {
    const value = this.checkpoints.get(key);
    return value ? structuredClone(value) as DurableCheckpoint<TPayload> : null;
  }

  async saveCheckpoint<TPayload>(
    expectedRevision: number,
    checkpoint: DurableCheckpoint<TPayload>,
  ): Promise<{ readonly ok: true } | { readonly ok: false; readonly code: 'REVISION_CONFLICT'; readonly actualRevision: number }> {
    const actualRevision = this.checkpoints.get(checkpoint.key)?.revision ?? 0;
    if (actualRevision !== expectedRevision) {
      return { ok: false, code: 'REVISION_CONFLICT', actualRevision };
    }
    this.checkpoints.set(checkpoint.key, structuredClone(checkpoint) as DurableCheckpoint<unknown>);
    return { ok: true };
  }

  async enqueue(record: OutboxRecord): Promise<void> {
    this.outbox.set(record.id, structuredClone(record));
  }

  async pending(): Promise<readonly OutboxRecord[]> {
    return [...this.outbox.values()]
      .filter((record) => record.status === 'pending')
      .map((record) => structuredClone(record));
  }

  async acknowledge(id: string, at: string): Promise<boolean> {
    const existing = this.outbox.get(id);
    if (!existing) return false;
    this.outbox.set(id, { ...existing, status: 'acknowledged', acknowledgedAt: at });
    return true;
  }

  async findOutbox(id: string): Promise<OutboxRecord | null> {
    const existing = this.outbox.get(id);
    return existing ? structuredClone(existing) : null;
  }
}

class DeviceGatePrintRepository implements PrintJobRepository {
  private readonly jobs = new Map<string, PrintJobSnapshot>();

  async getByDedupeKey(dedupeKey: string): Promise<PrintJobSnapshot | null> {
    const job = this.jobs.get(dedupeKey);
    return job ? structuredClone(job) : null;
  }

  async save(job: PrintJobSnapshot): Promise<void> {
    this.jobs.set(job.dedupeKey, structuredClone(job));
  }
}

class DeviceGatePrinterExecutionPort implements PrinterExecutionPort {
  async execute(request: PrinterExecutionRequest): Promise<PrinterExecutionResult> {
    return {
      executionId: request.executionId,
      status: 'failed',
      targets: request.targets.map((target) => ({
        targetRole: target.role,
        status: 'failed' as const,
        errorCode: 'DEVICE_GATE_PRINT_TEST_DOUBLE',
      })),
    };
  }
}

const causeCode = (error: unknown): string =>
  error instanceof Error ? error.message : 'SMT_BOOTSTRAP_UNKNOWN_ERROR';

export function createSmtDeviceGateBootstrap(seed: SmtDeviceGateBootstrapSeed): SmtRuntimeBootstrapResult {
  try {
    const seededAt = seed.now();
    const facts = createOperationalFacts();
    const staff = createStaffAuthority({ rootActorIds: [seed.rootActorId] });
    const roleId = `device-gate-role:${seed.operator.staffId}`;

    staff.createRole(
      { roleId, permissions: seed.operator.permissions },
      { actorId: seed.rootActorId, at: seededAt, idempotencyKey: `device-gate-role:${seed.operator.staffId}` },
    );
    staff.createStaff(
      { staffId: seed.operator.staffId, displayName: seed.operator.displayName, roleIds: [roleId] },
      { actorId: seed.rootActorId, at: seededAt, idempotencyKey: `device-gate-staff:${seed.operator.staffId}` },
    );

    const availability = createAvailabilityAuthority({
      storeId: seed.storeSettings.storeId,
      authorize: ({ actorId, permission, at }) => {
        staff.authorize({ actorId, permission, at });
      },
      getBasePolicy: () => seed.basePolicy,
      getStoreSettings: () => seed.storeSettings,
      appendFact: (fact) => {
        facts.append(fact);
      },
    });

    const payment = createPaymentAuthority();
    const table = createTableAuthority({
      storeId: seed.storeSettings.storeId,
      businessDate: seed.businessDate,
      tables: seed.tables,
      queueClasses: seed.queueClasses,
      authorize: ({ actorId, permission, at }) => {
        staff.authorize({ actorId, permission, at });
      },
      isOrderFullySettled: (orderId) => {
        try {
          return payment.snapshot(orderId).fullySettled;
        } catch {
          return false;
        }
      },
      facts,
    });

    const products = new Map(seed.products.map((product) => [product.id, product] as const));
    const adapterInputs: SmtRuntimeAdapterInputs = {
      operatorId: seed.operator.staffId,
      deviceId: seed.deviceId,
      categories: seed.categories,
      paymentMethods: seed.paymentMethods,
      ...(seed.initialServiceMode !== undefined ? { initialServiceMode: seed.initialServiceMode } : {}),
      ...(seed.defaultServiceModeBySource !== undefined
        ? { defaultServiceModeBySource: seed.defaultServiceModeBySource }
        : {}),
      businessDate: () => seed.businessDate,
      now: seed.now,
      nextId: seed.nextId,
    };

    return composeSmtBusinessRuntime({
      profile: 'device-gate',
      storeId: seed.storeSettings.storeId,
      businessDate: seed.businessDate,
      operatorId: seed.operator.staffId,
      deviceId: seed.deviceId,
      evidence: { durability: 'ephemeral', printExecution: 'test-double' },
      dependencies: {
        getActivePolicy: () => composeBusinessPolicyWithAvailability(seed.basePolicy, availability.projection()),
        getProduct: (productId) => products.get(productId) ?? null,
        isStoreReady: () => seed.storeReady,
        resolveFulfillmentPolicy: seed.resolveFulfillmentPolicy,
        payment,
        fulfillment: createFulfillmentAuthority(),
        print: createPrintAuthority(new DeviceGatePrintRepository(), new DeviceGatePrinterExecutionPort()),
        staff,
        table,
        availability,
        durability: createDurabilityCoordinator(new DeviceGateDurabilityPort()),
        facts,
        getStoreId: () => seed.storeSettings.storeId,
        getBusinessDate: () => seed.businessDate,
      },
      adapterInputs,
    });
  } catch (error: unknown) {
    return {
      kind: 'blocked',
      profile: 'device-gate',
      code: 'SMT_BOOTSTRAP_COMPOSITION_FAILED',
      causeCode: causeCode(error),
    };
  }
}
